<template>
  <div>
    <comment-sidebar />
    <h1>Histórico</h1>
    <div class="d-flex align-items-center justify-content-end m-2">
      <b-button variant="primary" @click="addComment">
        <feather-icon icon="PlusCircleIcon" class="mr-50" />
        <span class="text-nowrap">Novo comentário</span>
      </b-button>
    </div>
    <div v-if="loading" class="d-flex justify-content-center m-2">
      <b-spinner variant="primary" />
    </div>
    <template v-else-if="items.length > 0">
      <b-card no-body class="mb-0 m-2">
        <b-table
          :items="items"
          striped
          borderless
          :fields="tableColumns"
          show-empty
          empty-text="Não foram encontrados históricos"
          :busy="loading"
        >
          <!-- Column: createdAt -->
          <template #cell(created_at)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize">{{
                data.item.created_at | dateTime
              }}</span>
            </div>
          </template>
          <!-- Column: event -->
          <template #cell(event)="data">
            <div class="text-wrap">
              <span
                class="align-text-top text-indent"
                v-if="data.item.event_type === 'status_change'"
              >
                Mudança do status:
                <span class="text-primary">{{
                  data.item.previous_status.name
                }}</span>
                para:
                <span class="text-primary">{{
                  data.item.new_status.name
                }}</span>
              </span>
              <span
                class="align-text-top text-indent"
                v-else-if="data.item.event_type === 'comment'"
              >
                Comentário:
                <span class="text-primary">{{ data.item.comment }}</span>
              </span>
              <span
                class="align-text-top text-indent"
                v-else-if="data.item.event_type === 'closing_exception'"
              >
                <b-badge variant="light-warning">Alteração extraordinária</b-badge>
                Competência
                <span class="text-primary">{{
                  formatCompetence(data.item.competence)
                }}</span>
                <template v-if="data.item.ticket_number">
                  · Chamado
                  <span class="text-primary">{{ data.item.ticket_number }}</span>
                </template>
                <template v-if="data.item.reason">
                  · Motivo:
                  <span class="text-primary">{{ data.item.reason }}</span>
                </template>
              </span>
              <span
                class="align-text-top text-indent"
                style="white-space: pre-line;"
                v-else-if="data.item.event_type === 'pix_installment_plan_change'"
              >
                Pix Parcelado:
                <span class="text-primary">{{ data.item.comment }}</span>
              </span>
            </div>
          </template>
          <!-- Column: user -->
          <template #cell(user)="data">
            <div class="text-wrap">
              <span class="align-text-top text-capitalize" >{{
                data.item.user? data.item.user.name:'Sistema'
              }}</span>
            </div>
          </template>
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              right
              v-if="
                data.item.event_type === 'comment' && data.item.hasOwnership
              "
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item @click="remove(data.item.id)">
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">Remover</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-card>
    </template>
    <template v-else>
      <span class="d-flex justify-content-center">
        Não foram encontrados históricos
      </span>
    </template>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { avatarText } from "@core/utils/filter";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import { competenceLabel } from "../utils/contractLock";
import CommentSidebar from "@/modules/shared/components/CommentSidebar";
import useAppConfig from "@core/app-config/useAppConfig";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    vSelect,
    CommentSidebar,
  },
  setup() {
    const { skin } = useAppConfig();

    return { toast: useToast(), skin };
  },
  data() {
    return {
      loading: false,
      tableColumns: [
        { key: "created_at", label: "Data" },
        { key: "event", label: "Evento" },
        { key: "user", label: "Responsável" },
        { key: "Actions", label: "Ações" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      history: types.HISTORY,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    items: function () {
      if (this.history) {
        return _.orderBy(
          this.history,
          function (element) {
            return new Date(element.created_at);
          },
          ["desc"]
        );
      }
      return [];
    },
  },
  mounted() {
    this.get();
  },
  methods: {
    ...mapActions({
      getHistory: types.GET_HISTORY,
      deleteComment: types.DELETE_COMMENT,
      openCommentSidebar: sharedTypes.OPEN_COMMENT_SIDEBAR,
      saveComment: types.SAVE_COMMENT,
    }),
    // "2026-07" → "07/2026" (nunca via new Date: string ISO é lida como UTC)
    formatCompetence(competence) {
      return competenceLabel(competence) || "não informada";
    },
    get() {
      this.loading = true;
      this.getHistory({
        id: this.$route.params.id,
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar o histórico. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addComment(contractId) {
      this.openCommentSidebar({
        id: this.$route.params.id,
        title: "Adicionar comentário ao contrato " + this.$route.params.id,
        saveAction: this.save,
      });
    },
    async save(payload) {
      await this.saveComment(payload).then((resp) => {
        this.get();
        return resp;
      });
    },
    remove(idComment) {
      this.$swal({
        title: "Tem certeza?",
        text: `Deseja remover o comentário?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.deleteComment(idComment)
            .then((response) => {
              this.get();
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Sucesso",
                  text: `O comentário foi removido`,
                  icon: "CoffeeIcon",
                  variant: "success",
                },
              });
            })
            .catch((error) => {
              const status =
                error && error.response ? error.response.status : null;
              const errorText =
                status === 422
                  ? "Apenas o consultor que criou o comentario pode remover."
                  : "Ocorreu um erro ao remover o comentario. Entre em contato com o setor de TI.";
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Oops!",
                  text: errorText,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
